import { lazy, Suspense } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Spinner } from '../design-system/index.ts';
import { useSession } from '../lib/auth-client.ts';
import { AppShell } from './app-shell.tsx';
import { Logo, PhoneMock } from './brand.tsx';

// Chargés à la demande : un visiteur ne télécharge pas l'application, un connecté pas la présentation.
const LandingPage = lazy(async () => ({
  default: (await import('../features/landing/landing-page.tsx')).LandingPage,
}));
const DashboardPage = lazy(async () => ({
  default: (await import('../features/dashboard/dashboard-page.tsx')).DashboardPage,
}));

function FullScreenLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center text-accent">
      <Spinner size={24} />
      <span className="sr-only">Chargement</span>
    </div>
  );
}

/** Routes de l'application : session obligatoire. */
export function RequireAuth() {
  const { data, isPending } = useSession();
  const location = useLocation();
  if (isPending) return <FullScreenLoader />;
  if (!data) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/connexion?redirect=${redirect}`} replace />;
  }
  return <Outlet />;
}

/** Adresse racine : page de présentation pour les visiteurs, espace de création une fois connecté. */
export function HomeGate() {
  const { data, isPending } = useSession();
  if (isPending) return <FullScreenLoader />;
  if (!data)
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <LandingPage />
      </Suspense>
    );
  return (
    <AppShell>
      <Suspense fallback={null}>
        <DashboardPage />
      </Suspense>
    </AppShell>
  );
}

/** Pages d'authentification : le formulaire dans une carte, la démonstration à côté. */
export function AuthLayout() {
  const { data, isPending } = useSession();
  const location = useLocation();
  if (isPending) return <FullScreenLoader />;
  if (data && location.pathname !== '/reinitialiser-mot-de-passe') return <Navigate to="/" replace />;

  return (
    <div className="flex min-h-dvh flex-col px-4 py-6 sm:px-8">
      <a href="/" className="self-start rounded-lg">
        <Logo />
      </a>
      <div className="mx-auto flex w-full max-w-5xl flex-1 items-center gap-16 py-10">
        <main className="clay mx-auto w-full max-w-md p-6 sm:p-9 lg:mx-0">
          <Outlet />
        </main>
        <aside className="hidden flex-1 flex-col items-center gap-6 lg:flex">
          <PhoneMock className="w-[260px]" />
          <p className="max-w-xs text-center text-lg font-bold text-ink-2">
            Traduction, histoires, animations : ta vidéo prête à publier en quelques clics.
          </p>
        </aside>
      </div>
    </div>
  );
}
